import * as React from 'react'
import { KeyRound, Loader2, Pencil, Trash2, Unplug } from 'lucide-react'
import {
  useDeleteExternalMessagingTenantMutation,
  useListExternalMessagingChannelsQuery,
  useListIntegrationsQuery,
  useUpdateExternalMessagingChannelMutation,
  useUpdateIntegrationMutation,
  type ExternalMessagingChannel,
  type Integration,
  type MessagingPlatform
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Switch } from '../../../components/ui/switch'
import { ConfirmDialog } from '../../ConfigurePage/components/DeleteConfirmDialog'
import { IntegrationFormDialog } from './IntegrationFormDialog'
import { getIntegrationProvider } from './providers'
import { useIntegrationCredentialFlows } from './useIntegrationCredentialFlows'

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function IntegrationRow({
  integration,
  onEdit,
  onDelete
}: {
  integration: Integration
  onEdit: () => void
  onDelete: () => void
}) {
  const provider = getIntegrationProvider(integration.service)
  const Icon = provider?.Icon ?? KeyRound
  const label = provider?.label ?? integration.service
  const [updateIntegration, updateState] = useUpdateIntegrationMutation()
  const isServiceNow = integration.service === 'servicenow'
  const workOrdersOn = Boolean(integration.work_orders_enabled)

  const handleWorkOrdersToggle = async (checked: boolean) => {
    await updateIntegration({
      id: integration.id,
      patch: {
        work_orders_enabled: checked,
        ...(checked && !integration.config?.['table'] ? { config: { table: 'incident' } } : {})
      }
    }).unwrap()
  }

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-line bg-card px-3 py-2.5">
      <div className="flex items-center gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-ink-2">
          <Icon className="size-4.5" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-ink">
              {integration.instance_name}
            </span>
            <span className="text-xs text-ink-3">{label}</span>
          </div>
          <span className="truncate text-xs text-ink-3">
            {integration.api_key_name ? (
              <span className="inline-flex items-center gap-1">
                <KeyRound className="size-3" /> {integration.api_key_name}
              </span>
            ) : (
              'No API key configured'
            )}
            {integration.updated_at ? ` · updated ${formatDate(integration.updated_at)}` : ''}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Edit ${integration.instance_name}`}
            onClick={onEdit}
          >
            <Pencil />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Delete ${integration.instance_name}`}
            onClick={onDelete}
          >
            <Trash2 className="text-st-down" />
          </Button>
        </div>
      </div>

      {isServiceNow ? (
        <div className="ml-12 flex items-start justify-between gap-3 rounded-md border border-line bg-bg-sunk/40 px-3 py-2">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-xs font-medium text-ink">Show in technician work orders</span>
            <p className="text-11 leading-snug text-ink-3">
              When on, open ServiceNow incidents from this instance appear in the Work Orders list
              (and can be completed or commented on from Axilon). Agent-created work orders still
              stay in Axilon&apos;s internal list until create-into-ServiceNow ships.
            </p>
          </div>
          <Switch
            checked={workOrdersOn}
            disabled={updateState.isLoading}
            aria-label={`Show ${integration.instance_name} incidents in technician work orders`}
            onCheckedChange={(checked) => void handleWorkOrdersToggle(checked)}
          />
        </div>
      ) : null}
    </div>
  )
}

function channelLabel(channel: ExternalMessagingChannel): string {
  return channel.channel_name || channel.channel_id
}

interface ConnectedTenant {
  external_tenant_id: string
  external_tenant_name: string
  platform: MessagingPlatform
}

/** Distinct whitelisted tenants behind the connected channels, for de-listing. */
function distinctTenants(channels: ExternalMessagingChannel[]): ConnectedTenant[] {
  const seen = new Map<string, ConnectedTenant>()
  for (const channel of channels) {
    if (!channel.external_tenant_id || seen.has(channel.external_tenant_id)) continue
    seen.set(channel.external_tenant_id, {
      external_tenant_id: channel.external_tenant_id,
      external_tenant_name: channel.external_tenant_name || channel.external_tenant_id,
      platform: channel.platform
    })
  }
  return [...seen.values()]
}

function TeamsChannelsCard() {
  const {
    data: channels,
    isLoading,
    error
  } = useListExternalMessagingChannelsQuery({ platform: 'teams', page_size: 500 })
  const [updateChannel, updateState] = useUpdateExternalMessagingChannelMutation()
  const [deleteTenant, deleteState] = useDeleteExternalMessagingTenantMutation()
  const [disconnecting, setDisconnecting] = React.useState<ConnectedTenant | null>(null)

  const handleNotificationsTargetChange = async (
    channel: ExternalMessagingChannel,
    notificationsTarget: boolean
  ) => {
    await updateChannel({
      id: channel.id,
      patch: { notifications_target: notificationsTarget }
    }).unwrap()
  }

  const handleConfirmDisconnect = async () => {
    if (!disconnecting) return
    try {
      await deleteTenant({
        external_tenant_id: disconnecting.external_tenant_id,
        platform: disconnecting.platform
      }).unwrap()
    } finally {
      setDisconnecting(null)
    }
  }

  if (!isLoading && !error && (channels?.length ?? 0) === 0) {
    return null
  }

  const tenants = distinctTenants(channels ?? [])

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col">
          <h3 className="text-sm font-semibold text-ink">Teams Channels</h3>
          <p className="text-xs text-ink-3">
            Connected Microsoft Teams channels that can receive Axilon messages.
          </p>
        </div>

        {tenants.length > 0 ? (
          <div className="flex flex-col gap-2">
            {tenants.map((tenant) => (
              <div
                key={tenant.external_tenant_id}
                className="flex items-center gap-2 rounded-lg border border-line bg-card px-3 py-2"
              >
                <span
                  className="min-w-0 flex-1 truncate text-xs font-medium text-ink"
                  title={tenant.external_tenant_name}
                >
                  {tenant.external_tenant_name}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={deleteState.isLoading}
                  aria-label={`Disconnect ${tenant.external_tenant_name}`}
                  onClick={() => setDisconnecting(tenant)}
                >
                  <Unplug /> Disconnect
                </Button>
              </div>
            ))}
          </div>
        ) : null}

        {error ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            Failed to load Teams channels.
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
            <Loader2 className="size-3 animate-spin" />
            Loading Teams channels…
          </div>
        ) : null}

        {(channels?.length ?? 0) > 0 ? (
          <div className="overflow-hidden rounded-lg border border-line">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-line bg-bg-sunk text-11 uppercase tracking-[0.04em] text-ink-3">
                <tr>
                  <th className="px-3 py-2 font-semibold">Channel</th>
                  <th className="w-45 px-3 py-2 text-left text-12 font-semibold normal-case leading-tight tracking-normal">
                    Notifications Channel
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {channels?.map((channel) => (
                  <tr key={channel.id} className="bg-card">
                    <td className="truncate px-3 py-2 text-ink" title={channelLabel(channel)}>
                      {channelLabel(channel)}
                    </td>
                    <td className="px-3 py-2">
                      <label className="flex cursor-pointer select-none items-center gap-2 text-ink-2">
                        <Switch
                          checked={channel.notifications_target}
                          disabled={updateState.isLoading}
                          aria-label={`Notifications Channel for ${channelLabel(channel)}`}
                          onCheckedChange={(checked) =>
                            void handleNotificationsTargetChange(channel, checked)
                          }
                        />
                        <span className="text-11 text-ink-3">
                          {channel.notifications_target ? 'On' : 'Off'}
                        </span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardContent>

      <ConfirmDialog
        open={!!disconnecting}
        onOpenChange={(open) => {
          if (!open) setDisconnecting(null)
        }}
        title="Disconnect tenant"
        description={
          disconnecting
            ? `Disconnect "${disconnecting.external_tenant_name}" from your organization? This removes the tenant mapping, its connected channels, and the bot's conversation history. The tenant can reconnect later from the bot's login link.`
            : ''
        }
        confirmLabel="Disconnect"
        destructive
        busy={deleteState.isLoading}
        onConfirm={handleConfirmDisconnect}
      />
    </Card>
  )
}

export function IntegrationsTab() {
  const { data: integrations, isLoading, error } = useListIntegrationsQuery()
  const { deleteIntegrationWithSecret, isDeleting } = useIntegrationCredentialFlows()

  const [editing, setEditing] = React.useState<Integration | null>(null)
  const [deleting, setDeleting] = React.useState<Integration | null>(null)

  const handleConfirmDelete = async () => {
    if (!deleting) return
    try {
      await deleteIntegrationWithSecret(deleting)
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-col">
            <h3 className="text-sm font-semibold text-ink">Integrations</h3>
            <p className="text-xs text-ink-3">
              Manage connected providers. Add a ServiceNow instance from Ingest (New ingest or
              Modify site). API keys are stored encrypted and write-only.
            </p>
          </div>

          {error ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              Failed to load integrations.
            </div>
          ) : null}

          {isLoading ? (
            <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
              <Loader2 className="size-3 animate-spin" />
              Loading integrations…
            </div>
          ) : null}

          {!isLoading && !error && (integrations?.length ?? 0) === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-line px-3 py-8 text-center">
              <KeyRound className="size-5 text-ink-3" />
              <p className="text-sm text-ink-2">No integrations yet</p>
              <p className="max-w-xs text-xs text-ink-3">
                Add ServiceNow from Ingest → New ingest (or Modify site) → ServiceNow.
              </p>
            </div>
          ) : null}

          {(integrations?.length ?? 0) > 0 ? (
            <div className="flex flex-col gap-2">
              {integrations?.map((integration) => (
                <IntegrationRow
                  key={integration.id}
                  integration={integration}
                  onEdit={() => setEditing(integration)}
                  onDelete={() => setDeleting(integration)}
                />
              ))}
            </div>
          ) : null}
        </CardContent>

        {editing ? (
          <IntegrationFormDialog
            mode="edit"
            integration={editing}
            open={!!editing}
            onOpenChange={(open) => {
              if (!open) setEditing(null)
            }}
          />
        ) : null}

        <ConfirmDialog
          open={!!deleting}
          onOpenChange={(open) => {
            if (!open) setDeleting(null)
          }}
          title="Delete integration"
          description={
            deleting
              ? `Delete the ${getIntegrationProvider(deleting.service)?.label ?? deleting.service} integration "${deleting.instance_name}"? Its stored API key will also be removed.`
              : ''
          }
          confirmLabel="Delete"
          destructive
          busy={isDeleting}
          onConfirm={handleConfirmDelete}
        />
      </Card>

      <TeamsChannelsCard />
    </div>
  )
}
